import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { io, Socket } from "socket.io-client";
import { Inspection, UpdateInspectionRequest } from "../../types";
import { useTheme } from "../../contexts/ThemeContext";
import { useWorkshop } from "../../contexts/WorkshopContext";
import InspectionService from "../../services/inspectionService";
import Modal from "../common/Modal";
import DropdownMenu, { DropdownMenuItem } from "../common/DropdownMenu";

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || "http://localhost:8000";

const InspectionList: React.FC = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { selectedWorkshop } = useWorkshop();
  const { enqueueSnackbar } = useSnackbar();
  const [inspections, setInspections] = useState<Inspection[]>([]);
  const [filteredInspections, setFilteredInspections] = useState<Inspection[]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [socket, setSocket] = useState<Socket | null>(null);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    inspection: Inspection | null;
    action: "close" | "reopen" | "delete" | null;
  }>({
    isOpen: false,
    inspection: null,
    action: null,
  });

  useEffect(() => {
    if (selectedWorkshop) {
      loadInspections();
    }
  }, [selectedWorkshop]);

  useEffect(() => {
    // Connect to Socket.IO
    const newSocket = io(SOCKET_URL, {
      transports: ["websocket", "polling"],
    });

    newSocket.on("connect", () => {
      console.log("Connected to Socket.IO server");
    });

    newSocket.on(
      "inspection_updated",
      async (payload: { action: string; inspection_id?: number }) => {
        console.log("Inspection update received:", payload);

        if (payload.inspection_id) {
          try {
            // Check if the inspection is in the current list
            const existingIndex = inspections.findIndex(
              (insp) => insp.id === payload.inspection_id
            );

            if (existingIndex !== -1) {
              // Update only this inspection
              console.log(`Updating inspection ${payload.inspection_id}`);
              const updatedInspection =
                await InspectionService.getInspectionById(
                  payload.inspection_id
                );

              setInspections((prev) =>
                prev.map((insp) =>
                  insp.id === payload.inspection_id ? updatedInspection : insp
                )
              );
            } else {
              // New inspection, reload all
              console.log("New inspection detected, reloading");
              loadInspections();
            }
          } catch (error) {
            console.error("Error updating inspection:", error);
            // If error (e.g. inspection deleted), reload
            loadInspections();
          }
        } else {
          // Sin inspection_id, recargar todas
          loadInspections();
        }
      }
    );

    newSocket.on("disconnect", () => {
      console.log("Disconnected from Socket.IO server");
    });

    setSocket(newSocket);

    // Cleanup
    return () => {
      newSocket.close();
    };
  }, []);

  useEffect(() => {
    filterInspections();
  }, [inspections, searchTerm, statusFilter]);

  const loadInspections = async () => {
    if (!selectedWorkshop) {
      setLoading(false);
      setInspections([]); // Clear inspections when no workshop selected
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setInspections([]); // Clear previous workshop inspections immediately
      const data = await InspectionService.getInspections({
        workshop_id: selectedWorkshop.id,
        limit: 1000,
      });
      setInspections(data);
    } catch (error) {
      console.error("Error loading inspections:", error);
      setError("Error loading inspections");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (inspection: Inspection) => {
    const newStatus = inspection.status === "open" ? "closed" : "open";
    setConfirmModal({
      isOpen: true,
      inspection,
      action: newStatus === "closed" ? "close" : "reopen",
    });
  };

  const handleDelete = async (inspection: Inspection) => {
    setConfirmModal({
      isOpen: true,
      inspection,
      action: "delete",
    });
  };

  const handleConfirmAction = async (comment?: string) => {
    if (!confirmModal.inspection) return;

    const { inspection, action } = confirmModal;

    try {
      if (action === "delete") {
        await InspectionService.deleteInspection(inspection.id);
      } else {
        const newStatus = action === "close" ? "closed" : "open";
        await InspectionService.updateInspection(inspection.id, {
          status: newStatus,
          comment: action === "close" ? comment : undefined,
        });
      }
      await loadInspections();
      enqueueSnackbar(
        `Inspection ${
          action === "close"
            ? "closed"
            : action === "reopen"
            ? "reopened"
            : "deleted"
        } successfully`,
        { variant: "success" }
      );
    } catch (error) {
      console.error(`Error ${action}ing inspection:`, error);
      enqueueSnackbar(`Error ${action}ing inspection`, { variant: "error" });
    } finally {
      setConfirmModal({ isOpen: false, inspection: null, action: null });
    }
  };

  const handleCloseModal = () => {
    setConfirmModal({ isOpen: false, inspection: null, action: null });
  };

  const getModalConfig = () => {
    const { action, inspection } = confirmModal;
    if (!action || !inspection) return null;

    if (action === "close") {
      return {
        title: "Close Inspection",
        message: `Are you sure you want to close inspection #${inspection.id}? This action will update the inspection board.`,
        confirmText: "Close Inspection",
        type: "warning" as const,
        showCommentField: true,
        commentPlaceholder:
          "Add reason for closing or any final notes about this inspection...",
      };
    } else if (action === "reopen") {
      return {
        title: "Reopen Inspection",
        message: `Are you sure you want to reopen inspection #${inspection.id}?`,
        confirmText: "Reopen",
        type: "info" as const,
      };
    } else {
      return {
        title: "Delete Inspection",
        message: `Are you sure you want to delete inspection #${inspection.id}? This action cannot be undone.`,
        confirmText: "Delete",
        type: "danger" as const,
      };
    }
  };

  const filterInspections = () => {
    let filtered = inspections;

    if (searchTerm) {
      filtered = filtered.filter(
        (inspection) =>
          inspection.vehicle_id.toString().includes(searchTerm) ||
          inspection.created_by.toString().includes(searchTerm) ||
          inspection.id.toString().includes(searchTerm)
      );
    }

    if (statusFilter) {
      filtered = filtered.filter(
        (inspection) => inspection.status === statusFilter
      );
    }

    setFilteredInspections(filtered);
  };

  const getStatusColor = (status: string) => {
    return status === "open"
      ? "bg-green-100 text-green-800"
      : "bg-gray-100 text-gray-800";
  };

  const getStatusText = (status: string) => {
    return status === "open" ? "Open" : "Closed";
  };

  if (!selectedWorkshop) {
    return (
      <div className="mt-3">
        <div
          className={`rounded-lg shadow p-8 text-center ${
            isDarkMode ? "bg-gray-800" : "bg-white"
          }`}
        >
          <svg
            className={`mx-auto h-12 w-12 mb-4 ${
              isDarkMode ? "text-gray-600" : "text-gray-400"
            }`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h3
            className={`text-lg font-medium mb-2 ${
              isDarkMode ? "text-gray-300" : "text-gray-900"
            }`}
          >
            No Workshop Selected
          </h3>
          <p className={isDarkMode ? "text-gray-400" : "text-gray-600"}>
            Please select a workshop from the dropdown to view inspections.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3">
      {/* Filters */}
      <div
        className={`rounded-lg shadow p-4 mb-6 ${
          isDarkMode ? "bg-gray-800" : "bg-white"
        }`}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="md:col-span-2">
            <label
              className={`block text-sm font-medium mb-1 ${
                isDarkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              Search
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg
                  className={`h-5 w-5 ${
                    isDarkMode ? "text-gray-500" : "text-gray-400"
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search by vehicle ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`block w-full pl-10 pr-3 py-2 border rounded-md leading-5 focus:outline-none focus:ring-1 sm:text-sm ${
                  isDarkMode
                    ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:ring-orange-400 focus:border-orange-400"
                    : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-blue-500 focus:border-blue-500"
                }`}
              />
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <label
              className={`block text-sm font-medium mb-1 ${
                isDarkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={`block w-full py-2 px-3 border rounded-md shadow-sm focus:outline-none sm:text-sm ${
                isDarkMode
                  ? "bg-gray-700 border-gray-600 text-white focus:ring-orange-400 focus:border-orange-400"
                  : "bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500"
              }`}
            >
              <option value="">All</option>
              <option value="open">Open</option>
              <option value="closed">Closed</option>
            </select>
          </div>
        </div>

        {/* Clear Filters */}
        {(searchTerm || statusFilter) && (
          <div className="mt-4">
            <button
              onClick={() => {
                setSearchTerm("");
                setStatusFilter("");
              }}
              className={`text-sm ${
                isDarkMode
                  ? "text-orange-400 hover:text-orange-300"
                  : "text-blue-600 hover:text-blue-800"
              }`}
            >
              Clear all filters
            </button>
          </div>
        )}
      </div>

      {/* Loading */}
      {loading && (
        <div className="text-center py-12">
          <div
            className={`inline-block animate-spin rounded-full h-8 w-8 border-b-2 ${
              isDarkMode ? "border-orange-400" : "border-blue-600"
            }`}
          ></div>
          <p
            className={`mt-2 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
          >
            Loading inspections...
          </p>
        </div>
      )}

      {/* Error */}
      {error && (
        <div
          className={`border px-4 py-3 rounded mb-4 ${
            isDarkMode
              ? "bg-red-900/20 border-red-500 text-red-300"
              : "bg-red-50 border-red-400 text-red-700"
          }`}
        >
          {error}
        </div>
      )}

      {/* Results Count */}
      {!loading && !error && (
        <div
          className={`mb-4 text-sm ${
            isDarkMode ? "text-gray-400" : "text-gray-600"
          }`}
        >
          Showing {filteredInspections.length} of {inspections.length}{" "}
          inspections
        </div>
      )}

      {/* Table */}
      {!loading && !error && (
        <div
          className={`rounded-lg shadow overflow-hidden ${
            isDarkMode ? "bg-gray-800" : "bg-white"
          }`}
        >
          <div className="overflow-x-auto">
            <table
              className={`min-w-full divide-y ${
                isDarkMode ? "divide-gray-700" : "divide-gray-200"
              }`}
            >
              <thead className={isDarkMode ? "bg-gray-900" : "bg-gray-50"}>
                <tr>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Date
                  </th>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Vehicle
                  </th>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Status
                  </th>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody
                className={`divide-y ${
                  isDarkMode
                    ? "bg-gray-800 divide-gray-700"
                    : "bg-white divide-gray-200"
                }`}
              >
                {filteredInspections.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      className={`px-6 py-12 text-center ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      <svg
                        className={`mx-auto h-12 w-12 ${
                          isDarkMode ? "text-gray-600" : "text-gray-400"
                        }`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <p className="mt-2">No inspections found</p>
                    </td>
                  </tr>
                ) : (
                  filteredInspections.map((inspection) => (
                    <tr
                      key={inspection.id}
                      className={`${
                        inspection.status === "open"
                          ? isDarkMode
                            ? "bg-green-900/20 hover:bg-green-900/30 border-l-4 border-green-500"
                            : "bg-green-50 hover:bg-green-100 border-l-4 border-green-500"
                          : isDarkMode
                          ? "hover:bg-gray-700"
                          : "hover:bg-gray-50"
                      }`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div
                          className={`text-sm ${
                            isDarkMode ? "text-gray-200" : "text-gray-900"
                          }`}
                        >
                          {new Date(inspection.created_at).toLocaleDateString()}
                        </div>
                        <div
                          className={`text-xs ${
                            isDarkMode ? "text-gray-500" : "text-gray-500"
                          }`}
                        >
                          {new Date(inspection.created_at).toLocaleTimeString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div
                          className={`text-sm font-medium ${
                            isDarkMode ? "text-gray-200" : "text-gray-900"
                          }`}
                        >
                          Vehicle ID #{inspection.vehicle_id}
                          {inspection.vehicle_number && (
                            <span
                              className={`ml-2 text-sm ${
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }`}
                            >
                              No. {inspection.vehicle_number}
                            </span>
                          )}
                        </div>
                        <div
                          className={`text-xs ${
                            isDarkMode ? "text-gray-500" : "text-gray-500"
                          }`}
                        >
                          Created by: User #{inspection.created_by}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            inspection.status === "open"
                              ? "bg-green-100 text-green-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {inspection.status === "open" ? "Open" : "Closed"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <DropdownMenu
                          items={[
                            {
                              label: "View Details",
                              icon: (
                                <svg
                                  className="w-5 h-5"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                  />
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                  />
                                </svg>
                              ),
                              onClick: () =>
                                navigate(
                                  `/inspections/${inspection.id}/details`
                                ),
                            },
                            ...(inspection.status === "open"
                              ? [
                                  {
                                    label: "Edit Inspection",
                                    icon: (
                                      <svg
                                        className="w-5 h-5"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                        />
                                      </svg>
                                    ),
                                    onClick: () =>
                                      navigate(
                                        `/inspections/${inspection.id}/edit`
                                      ),
                                  },
                                ]
                              : []),
                            { divider: true } as DropdownMenuItem,
                            {
                              label:
                                inspection.status === "open"
                                  ? "Close Inspection"
                                  : "Reopen Inspection",
                              icon:
                                inspection.status === "open" ? (
                                  <svg
                                    className="w-5 h-5"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                  </svg>
                                ) : (
                                  <svg
                                    className="w-5 h-5"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                    />
                                  </svg>
                                ),
                              onClick: () => handleToggleStatus(inspection),
                              variant:
                                inspection.status === "open"
                                  ? "warning"
                                  : "success",
                            },
                            {
                              label: "Delete",
                              icon: (
                                <svg
                                  className="w-5 h-5"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                  />
                                </svg>
                              ),
                              onClick: () => handleDelete(inspection),
                              variant: "danger" as const,
                            },
                          ]}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmModal.isOpen && getModalConfig() && (
        <Modal
          isOpen={confirmModal.isOpen}
          onClose={handleCloseModal}
          onConfirm={handleConfirmAction}
          title={getModalConfig()!.title}
          message={getModalConfig()!.message}
          confirmText={getModalConfig()!.confirmText}
          cancelText="Cancel"
          type={getModalConfig()!.type}
          showCommentField={(getModalConfig() as any).showCommentField}
          commentPlaceholder={(getModalConfig() as any).commentPlaceholder}
        />
      )}
    </div>
  );
};

export default InspectionList;
